export const en = {
  app: {
    name: 'QPRO Connect',
    rootMissing: '[QPRO CONNECT] Missing #root element. Check index.html.',
    copyright: 'QPRO Connect. All rights reserved.',
  },
  nav: {
    home: 'Home',
    listings: 'Listings',
    companiesCatalog: 'Companies',
    faq: 'FAQ',
    contact: 'Contact',
    accountMenuAria: 'User account menu',
    openMenuAria: 'Open navigation menu',
    myAccount: 'My account',
    myListings: 'My listings',
    settings: 'Settings',
    logout: 'Log out',
    login: 'Log in',
    register: 'Register',
    platform: 'Platform',
    legal: 'Legal',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    footerDescription:
      'Platform for the transport industry: listings, companies, pilots, and drivers in one place.',
    language: 'Language',
    polish: 'Polski',
    english: 'English',
  },
  common: {
    loading: 'Loading...',
    retry: 'Try again',
    serverError:
      'A server error occurred (500). We are working on a fix. Please try again later.',
    displayedResults: 'Showing {{start}}-{{end}} of {{total}} results',
    pageOf: 'Page {{current}} of {{total}}',
    verified: 'Verified',
    highlighted: 'Highlighted',
    worker: 'Worker',
    unknownDate: 'Unknown date',
    unknownListing: 'Unknown listing',
    listingWithoutTitle: 'Untitled listing',
    unknownPilot: 'Unknown pilot',
    noAccess: 'No access',
    email: 'Email',
    phone: 'Phone',
    website: 'Website',
    location: 'Location',
    taxId: 'Tax ID',
    regon: 'REGON',
    active: 'Active',
    inactive: 'Inactive',
    noLocation: 'No location',
    cancel: 'Cancel',
    adding: 'Adding...',
    back: 'Back',
    fullScreen: 'Full screen',
  },
  status: {
    open: 'Open',
    closed: 'Closed',
    canceled: 'Canceled',
  },
  preferences: {
    location: 'Location',
    home: 'Home',
    work: 'Work',
    car: 'Car',
    explore: 'Explore',
    map: 'Map',
    title: 'Preferred locations',
    add: 'Add location',
    addDialogTitle: 'Add preferred location',
    info:
      'This data will be used by the smart matching algorithm to better fit listings in your area.',
    loadError: 'Failed to load preferred locations.',
    deleteConfirm: 'Are you sure you want to delete this location?',
    deleteSuccess: 'Location has been deleted.',
    deleteError: 'Failed to delete the location.',
    required: 'Title and city are required.',
    addSuccess: 'Location has been added.',
    addError: 'Failed to add the location.',
    empty: 'You have not added any preferred locations yet.',
    noLocation: 'No location',
    radius: 'radius',
    titleLabel: 'Title (e.g. Home, Base)',
    cityLabel: 'City',
    latitudeLabel: 'Latitude (Lat)',
    longitudeLabel: 'Longitude (Lng)',
    latitudePlaceholder: 'e.g. 52.2297',
    longitudePlaceholder: 'e.g. 21.0122',
    distanceLabel: 'Distance (km)',
    iconLabel: 'Icon',
  },
  auth: {
    checkingAccount: 'Checking account setup...',
    loginTitle: 'Log in',
    registerTitle: 'Register',
    email: 'Email address',
    password: 'Password',
    username: 'Username',
    signIn: 'Log in',
    createAccount: 'Create account',
    googleLogin: 'Continue with Google',
    googleRegister: 'Register with Google',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
  },
  account: {
    dashboard: 'Dashboard',
    myListings: 'My listings',
    favourites: 'Favourites',
    sentApplications: 'Your applications',
    myOrders: 'My orders',
    listingsAndOffers: 'Listings and offers',
    receivedApplications: 'Received offers',
    orders: 'Orders',
    preferences: 'Preferred locations',
    profile: 'My profile',
    messages: 'Messages',
    settings: 'Settings',
    userFallback: 'User',
    welcome: 'Welcome to QPRO Connect, {{username}}!',
    dashboardDescription:
      'From here you manage your listings, messages, and profile. Below you will find shortcuts to the most important sections.',
    activeListings: 'Active listings',
    unreadMessages: 'Unread messages',
    browseListings: 'Browse listings',
    addListing: 'Add listing',
    seeReceivedApplications: 'See received offers',
    loadingDashboard: 'Loading dashboard data...',
    loadingListings: 'Loading listings...',
    loadingProfile: 'Loading profile data...',
    loadingSentApplications: 'Loading your applications...',
    loadingMyOrders: 'Loading your orders...',
    loadingReceivedApplications: 'Loading received offers...',
    noListingsTitle: 'No listings',
    noListingsDescription:
      'You do not have any listings yet. Add your first listing to start attracting jobs.',
    noFavouritesTitle: 'No saved listings',
    noFavouritesDescription:
      'You do not have any saved listings yet. Browse the catalog and add listings to your list.',
    noMessagesTitle: 'No messages',
    noMessagesDescription:
      'You do not have any messages yet. Contact a company or pilot to start a conversation.',
    noSentApplicationsTitle: 'No sent applications',
    noSentApplicationsDescription:
      'You have not applied to any jobs yet. Browse listings and find something for yourself.',
    noMyOrdersTitle: 'No orders',
    noMyOrdersDescription:
      'You do not have any closed listings you have been assigned to yet.',
    noReceivedApplicationsTitle: 'No received offers',
    noReceivedApplicationsDescription:
      'Nobody has applied to your listings yet.',
    noReceivedForListingDescription:
      'Nobody has responded to this listing yet.',
    receivedCompanyOnly:
      'Received offers are available only for transport company accounts.',
    appliesToListing: 'Applies to listing:',
    forListing: 'Offers for listing',
    selectedListing: 'Selected listing',
    allApplications: 'All offers',
    pilot: 'PILOT',
    pilotMessage: 'MESSAGE FROM PILOT',
    pilotNoMessage: 'The pilot did not add a message.',
    receivedAt: 'Received: {{date}}',
    sentAt: 'Sent: {{date}}',
    netPrice: 'Net price',
    vat: 'VAT',
  },
  listings: {
    pageTitle: 'Transport listings',
    pageSubtitle:
      'Find a job or offer that matches your permissions and capabilities.',
    searchPlaceholder: 'Search by title or description',
    status: 'Status',
    dedication: 'Dedication',
    permissions: 'Permissions',
    sort: 'Sorting',
    filters: 'Filters',
    clearFilters: 'Clear filters',
    loading: 'Loading listings...',
    noResultsTitle: 'No listings',
    noResultsDescription: 'No listings matched the selected filters.',
    details: 'Details',
    favorite: 'Add to favourites',
    removeFavorite: 'Remove from favourites',
    favoriteLoginRequired: 'Log in to add a listing to favourites.',
    favoriteAdded: 'Added to favourites.',
    favoriteRemoved: 'Removed from favourites.',
    favoriteError: 'Failed to change favourite status.',
    apply: 'Apply',
    applyTo: 'You are applying to: {{title}}',
    applicationMessageHint:
      'Write a short message to the transport company introducing your offer.',
    sendApplication: 'Send offer',
    sending: 'Sending...',
    workersOnly: 'Workers only',
    detailedRoute: 'Detailed route',
    startPlace: 'Starting place',
    endPlace: 'Destination',
    map: 'Map',
    netValue: 'net value',
    amountNotProvided:
      'The listing does not include an amount. Please contact the company directly to start negotiations.',
    saveListing: 'Save listing',
    removeSavedListing: 'Remove saved listing',
    applyTitle: 'Apply to the job',
    messageRequired: 'Message content cannot be empty.',
    applicationSent: 'Your application has been sent!',
    applicationError: 'An error occurred while sending the application.',
    messageLabel: 'Your message',
    messagePlaceholder:
      'Hello, I am interested in this job. I have the required permissions and I am available within the given timeframe...',
    cancel: 'Cancel',
    newest: 'Newest',
    oldest: 'Oldest',
    sortValueDesc: 'Highest value',
    sortValueAsc: 'Lowest value',
    requiredPermissions: 'Required permissions',
    resultsSummary: 'Found {{total}} listings',
    searchPlaceholderExtended: 'Search by title, description, location...',
    noResultsLong:
      'No listings matched the provided criteria. Try adjusting the search filters.',
    pilots: 'Pilots',
  },
  filters: {
    title: 'Filters',
    city: 'City',
    cityPlaceholder: 'Enter city',
    nipPlaceholder: 'Enter Tax ID number',
    companyType: 'Company type',
    onlyVerified: 'Verified only',
    onlyHighlighted: 'Highlighted only',
    sort: 'Sorting',
    clear: 'Clear filters',
    pilotType: 'Pilot type',
    allTypes: 'All types',
    permissions: 'Permissions',
  },
  offline: {
    title: 'No connection',
    description:
      'Looks like we lost connection to the database. Check your internet connection and try again.',
    factTitle: 'Transport fact',
    refresh: 'Refresh page',
  },
  companies: {
    hubTitle: 'Companies and pilots directory',
    transportCompanies: 'Transport companies',
    pilots: 'Transport pilots',
    companyFallback: 'Transport company',
    pilotFallback: 'Pilot',
    noCompaniesTitle: 'No companies',
    noCompaniesDescription: 'No companies matched the selected filters.',
    noPilotsTitle: 'No pilots',
    noPilotsDescription: 'No pilots matched the selected filters.',
    companyListings: 'Company listings',
    noCompanyListingsTitle: 'No listings',
    noCompanyListingsDescription:
      'This company has not published any listings yet.',
    certificates: 'Certificates',
    gallery: 'Gallery',
    backToCompanies: 'Back to companies directory',
    backToCatalog: 'Back to catalog',
    seeCompanyProfile: 'See company profile',
    unverifiedCompany:
      'This company has not been verified by the QPRO Connect team',
    unverifiedPilot:
      'This pilot has not been verified by the QPRO Connect team',
    pageSubtitle:
      'A directory of verified transport companies operating across Poland.',
    searchPlaceholder:
      'Search by company name, Tax ID, or contact details...',
    resultsSummary: 'Found {{total}} companies',
  },
  pilots: {
    pageTitle: 'Transport pilots',
    pageSubtitle: 'A directory of certified pilots available in the system.',
    searchPlaceholder: 'Search by first or last name...',
    resultsSummary: 'Found {{total}} pilots',
    noPilotsLong: 'No pilots matched the provided criteria.',
    loadingProfile: 'Loading pilot profile...',
    notFound: 'Pilot not found.',
    backToCatalog: 'Back to pilots directory',
    noPermissions: 'No permissions assigned.',
    collaborateTitle: 'Start working with this pilot',
    collaborateDescription:
      'Contact {{name}} directly to discuss the job details.',
    call: 'Call',
    sendMessage: 'Send message',
  },
  maps: {
    location: 'Location',
    noGeodata: 'No geographic data available to display the map.',
    startLocation: 'Starting location',
    endLocation: 'Destination',
  },
  onboarding: {
    alreadyRegisteredTitle: 'You already have a registered company',
    alreadyRegisteredDescription:
      'Your account is already linked to an existing company or pilot profile. You cannot create another one.',
    goToDashboard: 'Go to dashboard',
    firstStepsTitle: 'First steps',
    firstStepsSubtitle:
      'Choose the account type that best matches your business to complete your profile setup.',
    companyTileTitle: 'Transport company',
    companyTileDescription:
      'Manage your fleet, add listings, and search for jobs for your vehicles.',
    pilotTileTitle: 'B2B pilot',
    pilotTileDescription:
      'Offer pilot services, manage your permissions, and win new jobs.',
    letsStart: "Let's start",
    companyTitle: 'Transport company registration',
    companySubtitle:
      'Fill in the details below to create your company profile in the system.',
    companySection: 'Company information',
    companyContactSection: 'Contact and address details',
    companyCreate: 'Create company profile',
    pilotTitle: 'B2B pilot registration',
    pilotSubtitle:
      'Fill in the details below to create your pilot profile in the system.',
    pilotPersonalSection: 'Personal details',
    pilotPermissionsSection: 'Permissions held',
    pilotCompanySection: 'Company details (B2B)',
    pilotCreate: 'Create pilot profile',
    saving: 'Saving...',
    fillName: 'Enter first and last name.',
    companyProfileError: 'An error occurred while creating the company profile.',
    pilotProfileError: 'An error occurred while creating the pilot profile.',
    enterCompanyName: 'Enter the company name.',
    invalidNip: 'Enter a valid Tax ID number.',
    invalidRegon: 'Enter a valid REGON number.',
    invalidEmail: 'Enter a valid email address.',
    invalidPhone: 'Enter a valid phone number or leave the field empty.',
    invalidWebsite: 'Enter a valid website address.',
    invalidCoordinates: 'Enter valid coordinates or leave both fields empty.',
  },
  content: {
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    faq: 'Frequently asked questions',
    contact: 'Contact',
  },
  forms: {
    companyName: 'Company name',
    description: 'Business description',
    firstName: 'First name',
    lastName: 'Last name',
    city: 'City',
    address: 'Address',
    contactEmail: 'Contact email',
    phoneNumber: 'Phone number',
    websiteUrl: 'Website',
    latitude: 'Latitude (Lat)',
    longitude: 'Longitude (Lng)',
    iban: 'IBAN account number',
    accountDetails: 'Account details',
    usernameRequired: 'Username *',
    emailRequired: 'Email address *',
    passwordRequired: 'Password *',
    repeatPassword: 'Repeat password *',
    saveChanges: 'Save changes',
    saving: 'Saving...',
  },
  register: {
    fillRequired: 'Fill in all required fields.',
    passwordPolicy:
      'Password must be at least 8 characters long and include one digit, one uppercase letter, one lowercase letter, and one special character.',
    passwordsMismatch: 'Passwords do not match.',
    acceptTerms: 'You must accept the terms and privacy policy.',
    error: 'An error occurred during registration. Please try again.',
    title: 'Register',
    subtitle: 'Join the QPRO Connect community',
    googleRegister: 'Register with Google',
    submitting: 'Registering...',
    alreadyHaveAccount: 'Already have an account?',
    acceptPrefix: 'I accept',
  },
  callback: {
    unsupportedProvider: 'This login provider is not supported.',
    cancelled: 'Google login was cancelled or rejected.',
    missingToken: 'Google callback token is missing. Please try again.',
    success: 'Logged in with Google.',
    backToLogin: 'Back to login',
    finishingTitle: 'Finishing login',
    finishingDescription: 'Secure data exchange with Google and Strapi is in progress.',
    failedPrefix: 'Could not complete Google login:',
    failedGeneric: 'Could not complete Google login. Please try again.',
  },
  notifications: {
    unexpectedError: 'An unexpected error occurred. Please try again.',
    profileUpdated: 'Profile has been updated.',
    invalidIban: 'Enter a valid IBAN number or leave the field empty.',
  },
  transportFacts: [
    'The largest load ever moved over land weighed more than 15,000 tonnes. It was a petrochemical reactor in Saudi Arabia.',
    'Polish roads are part of the TEN-T trans-European transport network, which spans more than 75,000 km.',
    'An oversized transport pilot must hold special qualifications and may escort vehicles up to 7 meters wide.',
    'The longest road train in the world measured 1,474 meters and had 113 trailers. It drove through Australia.',
    'More than 2 million oversized transport permits are issued in Poland every year.',
  ],
} as const;

export type EnglishLanguagePack = typeof en;
